import type { Messages } from "../messages";
import { plural } from "../types";

export const fr: Messages = {
  common: {
    copy: "Copier",
    copied: "Copié",
    optional: "Facultatif",
    show: "Afficher",
    hide: "Masquer",
    loading: "Chargement…",
    on: "Activé",
    off: "Désactivé",
  },

  language: {
    label: "Langue",
  },

  header: {
    encrypted: "chiffré de bout en bout",
  },

  bytes: {
    b: "{size} o",
    kb: "{size} Ko",
    mb: "{size} Mo",
    gb: "{size} Go",
  },

  duration: {
    standalone: {
      hours: plural({ one: "{count} heure", other: "{count} heures" }),
      days: plural({ one: "{count} jour", other: "{count} jours" }),
      weeks: plural({ one: "{count} semaine", other: "{count} semaines" }),
    },
    inSentence: {
      hours: plural({ one: "{count} heure", other: "{count} heures" }),
      days: plural({ one: "{count} jour", other: "{count} jours" }),
      weeks: plural({ one: "{count} semaine", other: "{count} semaines" }),
    },
  },

  create: {
    mode: { text: "Texte", file: "Fichier" },
    textPlaceholder: "Collez un mot de passe, une clé d'API, une clé privée…",

    generator: {
      open: "Générer un mot de passe",
      close: "Fermer le générateur",
      length: "Longueur",
      lowercase: "Minuscules (a–z)",
      uppercase: "Majuscules (A–Z)",
      digits: "Chiffres (0–9)",
      symbols: "Symboles (!@#…)",
      excludeAmbiguous: "Éviter les caractères ambigus (0/O, 1/l/I)",
      needCharset: "Choisissez au moins un type de caractère.",
      generate: "Générer et insérer",
      note: "Généré dans votre navigateur. Il est inséré à l’emplacement du curseur dans le champ ci-dessous, ou à la fin si vous n’en avez pas placé.",
    },

    dropzone: {
      title: "Déposez un fichier ici",
      hint: "ou {browse} — jusqu'à {size}",
      browse: "parcourir",
      note: "chiffré avant de quitter cet appareil",
      remove: "Retirer",
    },

    encStatus: "Chiffré dans votre navigateur. La clé vit dans le lien, jamais sur nos serveurs.",

    expiry: {
      label: "Expire après",
      custom: "Personnalisé",
      unitHours: "Heures",
      unitDays: "Jours",
      unitWeeks: "Semaines",
    },

    oneTime: {
      title: "Détruire après une consultation",
      description: "Le secret est effacé dès son ouverture. Une seconde visite ne verra rien.",
    },

    allowedEmail: {
      label: "Restreindre à une seule adresse e-mail",
      hint: "Toute personne qui ouvre le lien vérifie une adresse e-mail à l'aide d'un code à usage unique. Indiquer une adresse ici signifie que seule cette personne pourra passer.",
      placeholder: "seulement-cette-personne@exemple.com",
    },

    notifyEmail: {
      title: "Me prévenir en cas de consultation",
      description: "Un accusé de lecture part à chaque ouverture du secret, en indiquant l'adresse qui l'a ouvert.",
      label: "Envoyer l'accusé à",
      placeholder: "vous@exemple.com",
    },

    developer: {
      title: "Options pour développeurs",
      webhookUrlLabel: "URL du webhook",
      webhookUrlHint: "Nous envoyons un événement JSON en POST à la création et à chaque consultation.",
      webhookSecretLabel: "Secret de signature du webhook",
      webhookSecretPlaceholder: "au moins 16 caractères",
      webhookSecretHint:
        "Sert à signer la charge utile (HMAC-SHA256, envoyée dans l'en-tête {header}). Obligatoire dès qu'une URL de webhook est définie.",
    },

    policy: {
      open: "Ce lien expire dans {expiry}. Toute personne disposant du lien peut l'ouvrir après avoir vérifié une adresse e-mail.",
      openOneTime:
        "Ce lien expire dans {expiry} ou dès sa première ouverture, selon ce qui arrive en premier. Toute personne disposant du lien peut l'ouvrir après avoir vérifié une adresse e-mail.",
      restricted: "Ce lien expire dans {expiry}. Seul {email} peut l'ouvrir.",
      restrictedOneTime:
        "Ce lien expire dans {expiry} ou dès sa première ouverture, selon ce qui arrive en premier. Seul {email} peut l'ouvrir.",
    },

    submit: "Créer le lien secret",
    submitting: "Chiffrement…",

    error: {
      needText: "Saisissez le texte à partager.",
      needFile: "Choisissez un fichier à partager.",
      needNotifyEmail:
        "Saisissez une adresse pour l'accusé de lecture, ou désactivez la notification.",
      webhookSecretTooShort: "Le secret de signature du webhook doit comporter au moins 16 caractères.",
      generic: "Une erreur est survenue lors de la création du secret.",
    },

    result: {
      title: "Votre lien est prêt",
      subtitle: "Envoyez-le comme bon vous semble. Sans le fragment qui suit le dièse, le lien ne sert à rien.",
      linkCaption: "la partie couleur argile est la clé de déchiffrement — nous ne la recevons jamais",
      expires: "Expire",
      destroyAfterOneView: "Détruire après une consultation",
      restrictedTo: "Restreint à",
      anyVerifiedEmail: "Toute adresse vérifiée",
      createAnother: "En créer un autre",
    },
  },

  retrieve: {
    invalidLink: {
      title: "Lien invalide",
      body: "Il manque sa clé de déchiffrement à ce lien. Vérifiez que vous avez bien copié l'URL entière — tout ce qui suit le {hash} compris.",
      note: "Si vous avez rechargé cette page, rouvrez le lien d'origine : la clé est retirée de la barre d'adresse dès le chargement de la page, afin qu'elle ne se retrouve ni dans votre historique, ni dans une capture d'écran, ni dans quoi que ce soit que ce site rapporte.",
    },

    notFound: {
      title: "Secret introuvable",
      body: "Ce secret a expiré, a déjà été consulté (s'il s'agissait d'un secret à usage unique), ou n'a jamais existé.",
    },

    alreadyRetrieved: {
      title: "Déjà ouvert",
      body: "Il s'agissait d'un secret à usage unique, et quelqu'un l'a déjà ouvert. Il ne peut plus être lu — ni par vous, ni par personne d'autre.",
      note: "Si ce quelqu'un, c'était vous, et que le téléchargement ne s'est pas terminé, réessayez le lien dans les prochaines minutes. Passé ce délai, le secret est définitivement perdu et la personne qui vous l'a envoyé devra le partager à nouveau.",
    },

    decrypting: "Déchiffrement…",

    fatalError: {
      title: "Une erreur est survenue",
      cannotReachServer: "Impossible de joindre le serveur.",
      cannotRequestCode: "Impossible de demander un code de vérification.",
      cannotVerifyCode: "Impossible de vérifier le code.",
      decryptionFailed: "Le déchiffrement a échoué — le lien est peut-être corrompu ou incomplet.",
    },

    email: {
      eyebrow: "Secret en attente",
      title: "Quelqu'un vous a envoyé quelque chose",
      body: "Vérifiez votre adresse e-mail pour l'ouvrir. Le code est valable dix minutes.",
      fileLine: "Fichier : {filename} ({size}). ",
      label: "Votre adresse e-mail",
      placeholder: "vous@exemple.com",
      submit: "Envoyer le code",
      submitting: "Envoi…",
      restricted: "Ce secret est réservé à une adresse e-mail précise, et ce n'est pas celle-ci.",
      locked: "Trop de tentatives incorrectes. Demandez un nouveau code.",
    },

    otp: {
      title: "Saisissez votre code",
      body: "Nous avons envoyé un code à usage unique à {email}.",
      label: "Code à usage unique",
      submit: "Déverrouiller le secret",
      submitting: "Vérification…",
      oneTimeNote: "L'ouvrir le détruit. Copiez ce dont vous avez besoin avant de fermer l'onglet.",
      resend: "Renvoyer le code",
      resendIn: "Renvoi dans {seconds} s",
      incorrect: "Code incorrect.",
      incorrectWithAttempts: plural({
        one: "Code incorrect ({count} tentative restante).",
        other: "Code incorrect ({count} tentatives restantes).",
      }),
    },

    success: {
      textTitle: "Le secret",
      fileTitle: "Votre fichier",
      destroyedOnServer: "détruit sur le serveur",
      clickToReveal: "Cliquez pour révéler",
      sendOneBack: "En renvoyer un",
      fileDownloaded: "{filename} a été déchiffré dans votre navigateur, puis téléchargé.",
      destroyedNotice:
        "Ce secret a été détruit sur le serveur. Recharger cette page n'affichera rien. Vérifié en tant que {email} à {time}.",
    },
  },

  reply: {
    title: "Renvoyer quelque chose",
    intro:
      "Ceci sera envoyé à {email}, la seule personne à pouvoir l'ouvrir. Vous pouvez envoyer une seule réponse.",
    receiptTo: "L'accusé de lecture sera envoyé à {email}, l'adresse que vous venez de vérifier.",
    expired: {
      title: "Cette fenêtre de réponse est fermée",
      body: "Une réponse ne peut être envoyée que depuis la page où vous avez ouvert un secret, et seulement pendant un court moment après. Demandez à l'expéditeur de partager un nouveau lien.",
    },
  },

  auth: {
    signInRequired: "Une connexion est requise pour créer un nouveau lien.",
    expired: {
      title: "Votre session a expiré",
      body: "Vous avez été déconnecté parce que cette page est restée ouverte un certain temps. Reconnectez-vous pour créer un lien. Ce que vous aviez déjà saisi n'a pas été conservé.",
    },
    signIn: "Se connecter",
    signedInAs: "Connecté en tant que {email}",
    signOut: "Se déconnecter",
  },

  apiError: {
    rateLimited: "Trop de requêtes. Patientez un instant, puis réessayez.",
    tooLarge: "Ce fichier est trop volumineux pour être partagé.",
    unauthenticated: "Votre session a expiré. Reconnectez-vous pour créer un lien.",
    webhookHostNotPermitted: "Cette URL de webhook pointe vers une destination que ce serveur refuse de contacter.",
    webhooksDisabled: "Les webhooks sont désactivés sur ce serveur.",
    validationFailed: "Certains détails ci-dessus n'ont pas été acceptés. Vérifiez-les et réessayez.",
  },
};
